import { forwardRef, InputHTMLAttributes, ReactNode, useMemo, useState } from 'react';

import clsx from 'clsx';

import { Colors, sprinkles } from '../../tokens';
import { IconLoadingLine } from '../Icon';
import IconEyeFill from '../Icon/IconEyeFill';
import IconEyeOffFill from '../Icon/IconEyeOffFill';
import { Text } from '../Text';
import { inputAffix, inputElement, inputHeightStyle, inputIcon, inputWrapper } from './Input.css';
import { InputSize } from './types';
import { inputTextSize } from './utils';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  width?: string;
  error?: boolean;
  loading?: boolean;
  inputSize?: InputSize;
  inputType?: 'normal' | 'border' | 'transparent';
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  onEndIconClick?: () => void;
  prefix?: string;
  suffix?: string;
  wrapperClassName?: string;
  wrapperStyle?: React.CSSProperties;
  textAlign?: 'left' | 'center' | 'right';
  /** 포맷팅 함수: 표시용 포맷팅 (예: 1234567890 -> 123-45-67890) */
  formatter?: (value: string) => string;
  /** 포맷팅 해제 함수: 내부 값으로 변환 (예: 123-45-67890 -> 1234567890) */
  deformatter?: (value: string) => string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      width,
      error = false,
      loading = false,
      inputSize = 'm',
      inputType = 'normal',
      disabled = false,
      startIcon,
      endIcon,
      onEndIconClick,
      prefix,
      suffix,
      wrapperClassName,
      wrapperStyle,
      className,
      onFocus,
      onBlur,
      textAlign = 'left',
      type,
      value,
      onChange,
      formatter,
      deformatter,
      ...props
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const isPasswordInput = type === 'password';
    const actualInputType = showPassword ? 'text' : type;

    // formatter/deformatter가 있는 경우 내부적으로 포맷팅 해제된 값을 관리
    const hasFormatter = Boolean(formatter && deformatter);

    // 표시용 포맷팅된 값 - props의 value를 기반으로 계산
    const displayValue = useMemo(() => {
      if (value == null) {
        return undefined;
      }
      return hasFormatter ? formatter!(String(value)) : value;
    }, [hasFormatter, formatter, value]);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      if (hasFormatter) {
        // 포맷팅 해제된 값으로 변환
        const deformatted = deformatter!(e.target.value);

        // onChange 이벤트는 포맷팅 해제된 값으로 전달
        const syntheticEvent = {
          ...e,
          target: {
            ...e.target,
            value: deformatted,
          },
        } as React.FocusEvent<HTMLInputElement>;
        onBlur?.(syntheticEvent);
      } else {
        onBlur?.(e);
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (hasFormatter) {
        // 포맷팅 해제된 값으로 변환
        const deformatted = deformatter!(e.target.value);
        const cursorPos = e.target.selectionStart || 0;

        // 커서 위치 계산 (포맷팅으로 인한 문자 추가/제거 고려)
        const beforeCursor = e.target.value.substring(0, cursorPos);
        const deformattedBeforeCursor = deformatter!(beforeCursor);
        const newCursorPos = formatter!(deformattedBeforeCursor).length;

        // 커서 위치 복원
        setTimeout(() => {
          e.target.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);

        // onChange 이벤트는 포맷팅 해제된 값으로 전달
        const syntheticEvent = {
          ...e,
          target: {
            ...e.target,
            value: deformatted,
          },
        } as React.ChangeEvent<HTMLInputElement>;

        onChange?.(syntheticEvent);
      } else {
        onChange?.(e);
      }
    };

    const handleTogglePassword = () => {
      setShowPassword((prev) => !prev);
    };

    return (
      <div
        style={{ width: width ?? '100%', ...wrapperStyle }}
        className={clsx(
          inputWrapper({
            inputType,
            size: inputSize,
            error,
            focused: isFocused,
            disabled,
            hasStartIcon: loading || Boolean(startIcon),
            hasEndIcon: isPasswordInput || Boolean(endIcon),
          }),
          inputHeightStyle({ size: inputSize }),
          wrapperClassName,
        )}
      >
        {(loading || startIcon) && (
          <div className={inputIcon({ size: inputSize })}>
            {loading ? <IconLoadingLine spin /> : startIcon}
          </div>
        )}
        {prefix && (
          <Text textStyle={inputTextSize(inputSize)} className={inputAffix({ size: inputSize })}>
            {prefix}
          </Text>
        )}
        <input
          ref={ref}
          type={actualInputType}
          className={clsx(inputElement({ size: inputSize, textAlign }), className)}
          disabled={disabled}
          value={displayValue}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          {...props}
        />
        {suffix && (
          <Text textStyle={inputTextSize(inputSize)} className={inputAffix({ size: inputSize })}>
            {suffix}
          </Text>
        )}
        {(isPasswordInput || endIcon) && (
          <div
            className={clsx(inputIcon({ size: inputSize }), sprinkles({ cursor: 'pointer' }))}
            onClick={isPasswordInput ? handleTogglePassword : onEndIconClick}
          >
            {isPasswordInput ? (
              showPassword ? (
                <IconEyeOffFill fill={Colors.gray600} />
              ) : (
                <IconEyeFill fill={Colors.gray600} />
              )
            ) : (
              endIcon
            )}
          </div>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';
